import { lazy, Suspense, useEffect } from 'react'
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider
} from 'react-router-dom'
import MainApp from '../layout/main-app'
import ErrorPage from '../pages/error/error'
import AuthBackGround from '../layout/auth/helper/authBackGround'
import { useAuth } from '../context/AuthContext'
import { useTranslation } from 'react-i18next'
import 'dayjs/locale/fr'
import 'dayjs/locale/es'
import dayjs from 'dayjs'
import { ADMIN, logo } from '../consts'
import { UserModel } from '../service/user'

const router = (isLoggedIn: boolean, currentUser: UserModel) => {
  const Availability = lazy(() => import('../pages/Availability/Availability'))
  const Forget = lazy(() => import('../layout/auth/forget'))
  const Login = lazy(() => import('../layout/auth/login'))
  const Register = lazy(() => import('../layout/auth/register'))
  const OTPPage = lazy(() => import('../layout/auth/otpPage'))
  const Schedules = lazy(() => import('../pages/Schedules/Schedules'))
  const OvertimeRequestList = lazy(
    () => import('../pages/OvertimeRequest/OvertimeRequestList')
  )
  const ScheduleReport = lazy(
    () => import('../pages/ScheduleReport/ViewScheduleReport')
  )
  const ScheduleReportList = lazy(
    () => import('../pages/ScheduleReport/ScheduleReportList')
  )
  const Client = lazy(() => import('../pages/Client/Client'))
  const ClientDetails = lazy(() => import('../pages/Client/ClientDetails'))
  const Permissions = lazy(() => import('../pages/UserManagements/Role/Role'))
  const TeamMemberlist = lazy(
    () => import('../pages/UserManagements/TeamMember/TeamMemberlist')
  )
  const AddTeamMember = lazy(
    () => import('../pages/UserManagements/TeamMember/AddTeamMember')
  )
  const ChangePassword = lazy(() => import('../layout/auth/changePassword'))
  const AssignRolePermissions = lazy(
    () => import('../pages/UserManagements/Role/assignRolePermissions')
  )
  const Invoice = lazy(() => import('../pages/Finance/Invoice/Invoice'))
  const InvoiceForm = lazy(() => import('../pages/Finance/Invoice/InvoiceForm'))
  const InvoicePreview = lazy(
    () => import('../pages/Finance/Invoice/InvoicePreview')
  )
  const Payroll = lazy(() => import('../pages/Finance/Payroll/Payroll'))
  const PayrollDetails = lazy(
    () => import('../pages/Finance/Payroll/PayrollPreviewV2')
  )
  const PayrollForm = lazy(
    () => import('../pages/Finance/Payroll/PayrollFormV2')
  )
  const TimeOffCategory = lazy(
    () => import('../pages/Timeoff/category/TimeOffCategory')
  )
  const Service = lazy(() => import('../pages/Service/Service'))
  const Audit = lazy(() => import('../pages/Audit/Audit'))
  // const UnderDevelopment = lazy(() => import('../pages/error/underDevelopment'))
  const Profile = lazy(() => import('../pages/Profile/Profile'))
  // const PayrollReport = lazy(
  //   () => import('../pages/ReportsOld/Payroll/PayrollReport')
  // )
  // const InvoiceReport = lazy(
  //   () => import('../pages/ReportsOld/Invoice2/Invoice2Report')
  // )
  // const TimeSheetList = lazy(
  //   () => import('../pages/ReportsOld/TimeSheet/TimeSheetList')
  // )
  const Report = lazy(() => import('../pages/Reports/Report'))
  const Settings = lazy(() => import('../pages/Settings/Settings'))
  const TimeOffRequest = lazy(
    () => import('../pages/Timeoff/request/timeOffRequest')
  )
  const Dashboard = lazy(() => import('../pages/dashboard/dashboard'))
  const AcceptInvite = lazy(() => import('../layout/auth/AcceptInvite'))
  // const Chat = lazy(() => import('../pages/Communications/Chat/Chat'))
  const Expense = lazy(() => import('../pages/Finance/Expense/Expense'))
  const ExpenseForm = lazy(() => import('../pages/Finance/Expense/ExpenseForm'))
  const Payment = lazy(() => import('../pages/Finance/Payment/Payment'))
  // const Support = lazy(() => import('../pages/Support/List'))
  // const FAQ = lazy(() => import('../pages/FAQ/List'))
  return createBrowserRouter([
    {
      path: '/',
      element:
        isLoggedIn ?
          <MainApp />
        : <Navigate
            to={`/auth?redirect=${window.location.pathname + window.location.search}`}
          />,
      errorElement: <ErrorPage />,
      children: [
        {
          index: true,
          element: (
            <Navigate
              to={
                ADMIN.includes(currentUser?.role?.name || '') ? '/dashboard' : (
                  '/schedules'
                )
              }
              replace
            />
          )
        },
        {
          path: 'dashboard',
          element: <Dashboard />
        },
        {
          path: 'availabilities',
          children: [
            {
              path: '',
              element: <Availability />
            }
          ]
        },
        {
          path: 'users',
          children: [
            {
              path: '',
              element: <TeamMemberlist />
            },
            {
              path: 'edit/:user_id',
              element: <AddTeamMember />
            },
            {
              path: 'create',
              element: <AddTeamMember />
            },
            {
              path: 'roles',
              children: [
                {
                  path: '',
                  element: <Permissions />
                },
                {
                  path: 'permissions/:role_id',
                  element: <AssignRolePermissions />
                }
              ]
            }
          ]
        },
        {
          path: 'locations',
          children: [
            { path: '', element: <Client /> },
            { path: ':id', element: <ClientDetails /> }
          ]
        },
        {
          path: 'services',
          element: <Service />
        },
        {
          path: 'schedules',
          children: [
            { path: '', element: <Schedules /> },
            {
              path: 'overtime-requests',
              element: <OvertimeRequestList />
            },
            {
              path: 'schedule-reports',
              element: <ScheduleReportList />
            },
            {
              path: ':schedule_id',
              element: <Schedules />
            }
          ]
        },
        {
          path: 'timeoff',
          children: [
            {
              path: '',
              element: <TimeOffRequest />
            },
            {
              path: 'timeoff-categories',
              element: <TimeOffCategory />
            },
            {
              path: 'timeoff-requests',
              element: <TimeOffRequest />
            }
          ]
        },
        {
          path: 'finances',
          children: [
            {
              path: '',
              element: <Invoice />
            },
            {
              path: 'invoices',
              children: [
                {
                  path: 'create',
                  element: <InvoiceForm />
                },
                {
                  path: 'edit/:id',
                  element: <InvoiceForm />
                },
                {
                  path: 'preview/:id',
                  element: <InvoicePreview />
                },
                {
                  path: '',
                  element: <Invoice />
                }
              ]
            },
            {
              path: 'expenses',
              children: [
                {
                  path: 'create',
                  element: <ExpenseForm />
                },
                {
                  path: 'edit/:id',
                  element: <ExpenseForm />
                },
                {
                  path: '',
                  element: <Expense />
                }
              ]
            },
            {
              path: 'payrolls',
              children: [
                {
                  path: 'create',
                  element: <PayrollForm />
                },
                {
                  path: 'edit/:id',
                  element: <PayrollForm />
                },
                {
                  path: 'preview/:id',
                  element: <PayrollDetails />
                },
                {
                  path: '',
                  element: <Payroll />
                }
              ]
            },
            {
              path: 'payments',
              children: [
                {
                  path: '',
                  element: <Payment />
                }
              ]
            }
          ]
        },
        //   // {
        //   // 	path: 'Report',
        //   // 	children: [
        //   // 		{
        //   // 			path: 'report-schedule',
        //   // 			element: <ReportSchedule />,
        //   // 		},
        //   // 	],
        //   // },
        {
          path: 'settings',
          element: <Settings />
        },
        //   {
        //     path: 'support',
        //     element: <Support />
        //   },
        //   {
        //     path: 'faq',
        //     element: <FAQ />
        //   },

        //   {
        //     path: 'communications',
        //     // element: <UnderDevelopment />,
        //     children: [
        //       {
        //         path: 'chat',
        //         element: <Chat />
        //       },
        //       {
        //         path: 'FAQ’s',
        //         element: <UnderDevelopment />
        //       },
        //       {
        //         path: 'Announcement',
        //         element: <UnderDevelopment />
        //       },
        //       {
        //         path: '',
        //         element: <Chat />
        //       }
        //     ]
        //   },
        {
          path: 'audit-logs',
          element: <Audit />
        },

        {
          path: 'report',
          children: [
            {
              path: '',
              element: <Report />
            }
            // {
            //   path: 'timesheet',
            //   element: <TimeSheetList />
            // },
            // {
            //   path: 'invoice',
            //   element: <InvoiceReport />
            // },
            // {
            //   path: 'payroll',
            //   element: <PayrollReport />
            // },
            // {
            //   path: 'timesheets',
            //   element: <TimeSheetList />
            // }
          ]
        },
        {
          path: 'profile',
          element: <Profile />
        }
      ]
    },
    {
      path: '/frontend',
      errorElement: <ErrorPage />,
      element: <Frontend />,
      children: [
        {
          path: 'invoice/:credentials/:id',
          element: <InvoicePreview />
        },
        {
          path: 'report/:credentials/:id',
          element: <ScheduleReport />
        }
      ]
    },
    {
      path: '/register',
      errorElement: <ErrorPage />,
      element: <AuthBackGround />,
      children: [
        {
          path: '',
          element: <Register />
        }
      ]
    },
    {
      path: '/auth',
      element:
        isLoggedIn ?
          <Navigate
            to={
              window.location.search.includes('redirect=') ?
                window.location.search.split('redirect=')[1]
              : '/'
            }
          />
        : <AuthBackGround />,
      errorElement: <ErrorPage />,
      children: [
        {
          path: 'forget-password',
          element: <Forget />
        },
        {
          path: 'reset-password/:credentials',
          element: <ChangePassword />
        },
        {
          path: 'accept/:invite_credentials',
          element: <AcceptInvite />
        },
        {
          path: 'otp/:email',
          element: <OTPPage />
        },
        {
          path: 'login',
          element: <Login />
        },
        {
          path: '',
          element: <Login />
        }
      ]
    }
  ])
}
export default function Router() {
  const { i18n, t } = useTranslation()
  useEffect(() => {
    document.documentElement.setAttribute('lang', i18n.language)
    dayjs.locale(i18n.language)
  }, [i18n.language])
  const changeLanguage = (lng: string) => i18n.changeLanguage(lng)
  const { isLoggedIn, currentUser } = useAuth()
  return (
    <>
      <RouterProvider router={router(isLoggedIn, currentUser)} />
      <footer
        className={`fixed bg-white text-center text-sm w-full bottom-0 p-4 z-10`}
      >
        {t('Copyright')} © {new Date().getFullYear()} Velocity Manager.{' '}
        {t('All rights reserved')}
        <select
          onChange={(e: any) => changeLanguage(e.target.value)}
          value={i18n.language}
          className='no-print sm:float-right py-0 align-baseline text-sm text-gray-500 bg-white border border-gray-300 rounded-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary'
        >
          <option value='en'>English</option>
          <option value='fr'>French</option>
          <option value='es'>Spanish</option>
        </select>
      </footer>
    </>
  )
}
const Frontend = () => {
  const { t } = useTranslation()
  return (
    <div className='min-h-screen flex items-center justify-center bg-gray-50 p-4'>
      <Suspense fallback={<div className='text-center'>{t('Loading')}</div>}>
        <div className='w-full max-w-7xl'>
          <a href='https://velocitymgr.com'>
            <img className='h-10 mx-auto my-5' src={logo} alt='' />
          </a>
          <Outlet />
        </div>
      </Suspense>
    </div>
  )
}
